import { useEffect } from "react";
import LAHeroSection from "./components/HeroSection";
import LACitiesDirectory from "./components/CitiesDirectory";
import LAStateResources from "./components/StateResources";
import LAFAQSection, { faqs } from "./components/FAQSection";
import LAFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function LouisianaPage() {
  useEffect(() => {
    document.title = "Louisiana Passport Services — Expedited & Emergency | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/louisiana/";
    setLink("canonical", pageUrl);
    setMeta("description", "Expedited passport services across Louisiana — New Orleans, Baton Rouge, Shreveport, Lafayette, Lake Charles. Emergency processing as fast as 24 hours. A+ BBB rated courier service.");
    setMeta("geo.placename", "Louisiana, United States");
    setMeta("geo.region", "US-LA");
    setMeta("geo.position", "30.9843;-91.9623");
    setMeta("ICBM", "30.9843, -91.9623");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens." },
      { name: "New Passport", description: "First-time passport application assistance." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service." },
      { name: "Child Passport", description: "Passport services for minors under 16." },
      { name: "Name Change", description: "Passport correction and name change service." },
      { name: "Second Passport & Damaged", description: "Second passport issuance and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Louisiana Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center serves Louisiana travelers with expedited and emergency passport processing.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1629 K Street, Suite 300",
          addressLocality: "Washington",
          addressRegion: "DC",
          postalCode: "20006",
          addressCountry: "US",
        },
        areaServed: { "@type": "State", name: "Louisiana" },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "State", name: "Louisiana" },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <LAHeroSection />
      <LACitiesDirectory />
      <LAStateResources />
      <LAFAQSection />
      <SEOCrossLinks state="LA" />
      <LAFinalCTA />
    </div>
  );
}